"use client";

import { SocialComposerField, SocialPostCard } from "@webdo/community-ui";
import { useEffect, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";
import { communityErrorKey, communityJson, communityRequest, type Discussion } from "../lib/community";
import styles from "./Community.module.css";

export function CommunityDiscussionEditor({ discussion, authorName, onSaved, onCancel }: { discussion?: Discussion; authorName?: string | null; onSaved: (discussion: Discussion) => void; onCancel: () => void }) {
  const t = usePortalTranslations();
  const [title, setTitle] = useState(discussion?.title ?? "");
  const [body, setBody] = useState(discussion?.body ?? "");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<ReturnType<typeof communityErrorKey> | null>(null);
  const dirty = title !== (discussion?.title ?? "") || body !== (discussion?.body ?? "");
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  return <form className={styles.discussionComposer} aria-label={t(discussion ? "community.edit" : "community.new")} onSubmit={async (event) => {
    event.preventDefault(); if (submitting.current || !title.trim() || !body.trim()) return;
    submitting.current = true;
    setBusy(true); setError(null);
    try {
      const saved = await communityRequest<Discussion>(discussion ? `discussions/${discussion.uuid}` : "discussions", communityJson(discussion ? "PUT" : "POST", { title: title.trim(), body: body.trim() }));
      onSaved(saved);
    } catch (failure) { setError(communityErrorKey(failure)); } finally { submitting.current = false; setBusy(false); }
  }}>
    <SocialPostCard
      authorName={discussion?.authorName || authorName || t("community.you")}
      metadata={t("community.publicDiscussion")}
      title={<SocialComposerField variant="title" label={t("community.title")} placeholder={t("community.titlePlaceholder")} value={title} maxLength={200} required disabled={busy} autoFocus onChange={(event) => setTitle(event.target.value)} />}
      body={<SocialComposerField variant="body" label={t("community.body")} placeholder={t("community.body")} value={body} maxLength={20000} required disabled={busy} onChange={(event) => setBody(event.target.value)} />}
      footer={<div className={styles.composerFooter}>{error ? <p role="alert" className={styles.error}>{t(error)}</p> : null}<div className={styles.actions}><button type="button" className={styles.secondary} disabled={busy} onClick={() => { if (!dirty || window.confirm(t("community.discard"))) onCancel(); }}>{t("community.cancel")}</button><button type="submit" className={styles.primary} disabled={busy || !title.trim() || !body.trim()}>{t(busy ? "community.saving" : discussion ? "community.save" : "community.create")}</button></div></div>}
    />
  </form>;
}
